/* Copyright (c) 2020-present CloudIO Inc */

import { Property } from 'csstype';
import { pageAtom } from 'designer/atoms';
import {
  AnalyzeContext,
  AnalyzeIssue,
  AnalyzeResult,
} from 'layout/Sidebar/panels/TreeView/AnalyzeUtils';
import { BoxItem } from './Box';

function isPercent(value?: Property.Width) {
  if (typeof value !== 'string') return false;
  if (value.endsWith('%')) {
    const num = Number(value.slice(0, -1));
    return num > 0 || num <= 100;
  }
  const num = Number(value);
  return num > 0 || num <= 1;
}

function getPercentString(value?: Property.Width) {
  if (typeof value !== 'string') {
    throw new Error(
      `Invalid value ${value} of type ${typeof value}. Expected width in string with % or px or 0-1.`,
    );
  }
  if (value.endsWith('%')) {
    const num = Number(value.slice(0, -1));
    return num > 0 || num <= 100 ? `${num}%` : '100%';
  }
  const num = Number(value);
  return num > 0 || num <= 1 ? `${num * 100}%` : '100%';
}

function isPixel(value?: Property.Width) {
  if (typeof value !== 'string') return false;
  return value.endsWith('px');
}

export async function analyzeBox({
  platform,
  appUid,
  pageId,
  itemId,
  mgr,
}: AnalyzeContext): Promise<AnalyzeResult> {
  const alerts: AnalyzeIssue[] = [];
  const autoFixes: (() => void)[] = [];
  const page = platform.getPage(appUid, pageId);
  if (!page) {
    return null;
  }
  const item = page[itemId] as BoxItem;
  const { boxWidth, minWidth, maxWidth } = item.props;

  if (isPercent(boxWidth) || isPercent(minWidth) || isPercent(maxWidth)) {
    let breakOut = false;
    let paddingStr = '';
    const parentBox = platform.findParent(appUid, pageId, itemId, (parent) => {
      if (breakOut) return false;
      if (parent.type === 'Box') {
        const {
          padding,
          boxWidth: parentBoxWidth,
          minWidth: parentMinWidth,
          maxWidth: parentMaxWidth,
        } = (parent as BoxItem).props;
        const p =
          typeof padding === 'number'
            ? padding
            : padding?.all || padding?.left || padding?.right || 0;
        if (p > 0) {
          paddingStr = `${p}px`;
          return true;
        }
        if (
          isPixel(parentBoxWidth) ||
          isPixel(parentMinWidth) ||
          isPixel(parentMaxWidth)
        ) {
          // parent box has fixed width in pixels and without any padding, so we are ok
          breakOut = true;
          return false;
        }
      } else {
        breakOut = true;
      }
      return false;
    });
    if (parentBox) {
      let boxWidthStr = '';
      let minWidthStr = '';
      let maxWidthStr = '';
      if (isPercent(boxWidth)) {
        boxWidthStr = `calc(${getPercentString(boxWidth)} - ${paddingStr})`;
      }
      if (isPercent(minWidth)) {
        minWidthStr = `calc(${getPercentString(minWidth)} - ${paddingStr})`;
      }
      if (isPercent(maxWidth)) {
        maxWidthStr = `calc(${getPercentString(maxWidth)} - ${paddingStr})`;
      }

      alerts.push({
        key: `box-width-${itemId}`,
        showItemId: itemId,
        alert: `Box ${
          isPercent(boxWidth)
            ? 'Width'
            : isPercent(minWidth)
            ? 'Min Width'
            : 'Max Width'
        } is set to a percent value ${
          isPercent(boxWidth)
            ? boxWidth
            : isPercent(minWidth)
            ? minWidth
            : maxWidth
        }, but parent Box has padding!`,
        itemId,
        tip: `When you use percent values for Box Width, Min Width, or Max Width, 
you must make sure that the parent Box has no padding. 
Otherwise, the Box will be rendered with a width that is larger than the parent Box. 
In this case, you can either remove the padding from the parent Box, or use calc(100% - ${paddingStr}) 
for Box Width, Min Width, or Max Width. Click Fix It to automatically set the ${
          boxWidthStr ? `Box Width to ${boxWidthStr}` : ''
        } ${minWidthStr ? `Min Width to ${minWidthStr}` : ''} ${
          maxWidthStr ? `Max Width to ${maxWidthStr}` : ''
        }.`,
        fix: () => {
          const _page = { ...platform.getPage(appUid, pageId) };
          const _item = _page[itemId] as BoxItem;
          let updatedItem = _item;
          if (boxWidthStr) {
            updatedItem = {
              ...updatedItem,
              props: {
                ...updatedItem.props,
                boxWidth: boxWidthStr,
              },
            };
          }
          if (minWidthStr) {
            updatedItem = {
              ...updatedItem,
              props: {
                ...updatedItem.props,
                minWidth: minWidthStr,
              },
            };
          }
          if (maxWidthStr) {
            updatedItem = {
              ...updatedItem,
              props: {
                ...updatedItem.props,
                maxWidth: maxWidthStr,
              },
            };
          }
          _page[_item.itemId] = updatedItem;
          platform.setter(pageAtom, _page, {
            params: { appUid, pageId },
          });
        },
      });
    }
  }

  return [alerts, autoFixes];
}
